import { hooks } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { fade, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { footerHeight } from 'components/InCall/CallFooter';
import { FLAG_ENABLE_LIVE_CAPTIONS } from 'constants/FeatureFlags';
import NotificationPillContainer from 'containers/NotificationPill/NotificationPillContainer';
import useFeatureFlag from 'hooks/useFeatureFlag';
import React, { useEffect, useState } from 'react';

let captionsTimeout: ReturnType<typeof setTimeout>;
const CAPTIONS_TIMEOUT_DURATION = 5000;

const CallMessaging = () => {
  const liveCaptionsFlagEnabled = useFeatureFlag(FLAG_ENABLE_LIVE_CAPTIONS);
  const captionsEnabledStatus = hooks.useCaptionsEnabledStatus();
  const [lastCaption, setLastCaption] = useState(false);
  const [lastCaptionIsFinal, setLastCaptionIsFinal] = useState(false);
  const [captionCutoff, setCaptionCutoff] = useState(0);
  const captions = hooks
    .useRecentCaptions()
    .filter((caption) => !!caption)
    .slice(0, 3) // limit to most recent 3
    .filter((caption) => caption.timestamp > captionCutoff);
  const classes = useStyles();

  useEffect(() => {
    const lastMessage = captions[0]?.text || '';
    const lastMessageIsFinal = !!captions[0]?.final;
    const lastMessageTimestamp = captions[0]?.timestamp;
    if (lastMessage !== lastCaption || lastMessageIsFinal !== lastCaptionIsFinal) {
      // most recent message has changed, so reset timeout
      clearTimeout(captionsTimeout);
      setLastCaption(lastMessage);
      setLastCaptionIsFinal(lastMessageIsFinal);
      if (lastMessageIsFinal) {
        captionsTimeout = setTimeout(() => {
          setCaptionCutoff(lastMessageTimestamp);
        }, CAPTIONS_TIMEOUT_DURATION);
      }
    }
  }, [captions, lastCaption, lastCaptionIsFinal]);

  useEffect(() => {
    return () => {
      clearTimeout(captionsTimeout);
    };
  }, []);

  return (
    <div className={classes.container}>
      <NotificationPillContainer inCall />
      {liveCaptionsFlagEnabled && captionsEnabledStatus && !!captions.length && (
        <div className={classes.captionsContainer}>
          {captions
            .slice(0)
            .reverse()
            .map((caption, index) => (
              <div className={classes.caption} key={`caption-${index}`}>
                <strong>{caption.participantName}</strong>:&nbsp;
                <span className={clsx(!caption.final && classes.wip)}>{caption.text}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    alignItems: 'center',
    bottom: footerHeight,
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    left: 0,
    position: 'absolute',
    right: 0,
    zIndex: 11
  },
  captionsContainer: {
    alignItems: 'flex-start',
    backgroundColor: fade(colors.headlineBlack, 0.7),
    borderRadius: theme.spacing(3),
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    maxWidth: 660, // currently matches inCall pill notification maxWidth
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`
  },
  caption: {
    color: colors.white,
    fontSize: 14,
    textAlign: 'left'
  },
  wip: {
    fontStyle: 'italic'
  }
}));

export default CallMessaging;
